import React, { useState, useRef } from "react";
import SyntaxHighlighter from 'react-syntax-highlighter';
import { Form, Table } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import { saveAs } from "file-saver";
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { PageLoader } from "../../../../Components/NoDataFoundTable/NoDataFoundTable";

const ProjectLogConsole = ({ projectLogs, ids, logLoader, isFullScreenMode, isFullScreen, setIsFullScreen }) => {
  const [fontSize, setFontSize] = useState(14);
  const logRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleZoomIn = () => {
    setFontSize((prev) => Math.min(prev + 2, 32));
  };

  const handleZoomOut = () => {
    setFontSize((prev) => Math.max(prev - 2, 8));
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const secondMinuteCalculate = (totalSeconds) => {
    if (totalSeconds >= 60) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = (totalSeconds % 60);
      return `${minutes}min ${seconds}sec`;
    } else {
      return `${totalSeconds}sec`;
    }
  }

  let totalRunDuration = projectLogs?.logData?.procedure?.map(item => item.runDuration)?.reduce((sum, num) => sum + num, 0)

  const logTextFormatData = () => {
    let content = `=============== ${ids?.reportName} ===============\n\n`;
    content += `NOTE: All Procedure run process time: ${secondMinuteCalculate(projectLogs?.logData?.runDuration)}.\n\n`;

    projectLogs?.logData?.procedure?.forEach((item, index) => {
      content += `PROCEDURE ${index + 1}:\n`;
      content += `NOTE: Procedure ${index + 1} run process time: ${secondMinuteCalculate(item?.runDuration)}.\n`;

      if (item?.exitCode === "result") {
        content += `NOTE: This procedure has been successfully executed.\n\n`;
      } else if (item?.exitCode === "error") {
        content += `NOTE: This procedure did not execute because there was an error in the procedure. Please check it now.\n\n`;
      } else {
        content += `NOTE: This procedure exited with status: ${item?.exitCode || "unknown"}.\n\n`;
      }

      content += `CODE:\n${item?.code || "N/A"}\n\n`;

      if (item?.exitCode === "error" && Array.isArray(item?.syntax)) {
        item.syntax.forEach((line) => {
          content += `ERROR: ${line}\n`;
        });
        content += `\n`;
      }

      if (item?.INTABLE?.libraryName) {
        content += `NOTE: Read Dataset\n`;
        content += "----------------------------------------\n";
        content += `| ${item?.INTABLE?.libraryName.padEnd(36)} |\n`;
        content += "----------------------------------------\n";
        content += `| Observations | ${String(item?.INTABLE?.observations).padEnd(20)}|\n`;
        content += `| Variables    | ${String(item?.INTABLE?.variable).padEnd(20)}|\n`;
        content += "----------------------------------------\n\n";
      }

      if (item?.OUTTABLE?.libraryName) {
        content += `NOTE: Result Dataset\n`;
        content += "----------------------------------------\n";
        content += `| ${item?.OUTTABLE?.libraryName.padEnd(36)} |\n`;
        content += "----------------------------------------\n";
        content += `| Observations | ${String(item?.OUTTABLE?.observations).padEnd(20)}|\n`;
        content += `| Variables    | ${String(item?.OUTTABLE?.variable).padEnd(20)}|\n`;
        content += "----------------------------------------\n\n";
      }
    });

    return content
  };

  const downloadLogTextFile = () => {
    let content = logTextFormatData()
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${ids?.reportName}.txt`);
  };

  const handleCopy = async () => {
    try {
      let textToCopy = logTextFormatData()
      if (textToCopy) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="overflow-hidden w-100">
      <div
        className="justify-content-between toolbar border-bottom hstack w-100 p-1"
        style={{ height: 35 }}
      >
        <div className="hstack gap-1">
          <i className="fa-solid fa-file-lines"></i>
          <h6 className="m-0">{ids?.name} Logs</h6>
        </div>
        <div className="hstack gap-1">
          {(projectLogs?.logData?.procedure || [])?.length > 0 &&
            <>
              <i
                title="Zoom IN"
                className="fa-solid fa-plus CP"
                onClick={handleZoomIn}
              />
              <div className="vr" />
              <i
                title="Zoom OUT"
                className="fa-solid fa-minus CP"
                onClick={handleZoomOut}
              />
              <div className="vr" />
              <i
                title="Copy Logs"
                className={`fa-solid ${copied ? "fa-circle-check" : "fa-copy"} CP`}
                onClick={handleCopy}
              />{" "}
              <span>{copied && "Copied"}</span>
              <div className="vr" />
              <i
                title="Logs Text File Download"
                className="fa-solid fa-download CP"
                onClick={() => downloadLogTextFile(projectLogs)}
              />
              <div className="vr" />
              <i
                title="Print Logs"
                className="fa-solid fa-print CP"
                onClick={handlePrint}
              />
              <div className="vr" />
            </>
          }
          <i title={`${isFullScreen ? "Minimize" : "Maximize"} Console`} className={`fa-solid ${isFullScreen ? "fa-minimize" : "fa-maximize"} CP`} onClick={() => setIsFullScreen(!isFullScreen)} />
        </div>
      </div>
      <div
        ref={logRef}
        className="p-2"
        style={{
          height: isFullScreenMode
            ? "calc(100vh - 70px)"
            : "calc(100vh - 216px)",
          overflowY: "auto",
          fontSize: `${fontSize}px`,
          fontFamily: "monospace",
        }}
      >
        {logLoader ? (
          <PageLoader/>
        ) : (
          (projectLogs?.logData?.procedure || [])?.length > 0 ?
            <div ref={componentRef} className="px-2">
              <div className="text-center">
                <h6 className="fw-bold">
                  ======= {ids?.reportName} =======
                </h6>
                <div className="text-start">
                  <Form.Label
                    className="m-0"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    <b>NOTE:</b> {`All Procedure run process time: ${secondMinuteCalculate(totalRunDuration)}.`}
                  </Form.Label>
                </div>
                {projectLogs?.logData?.procedure?.map((item, index) => (
                  <div className="text-start mt-2">
                    <h8 className={`fw-bold ${item?.exitCode === "result" ? "text-success" : item?.exitCode === "error" ? "text-danger" : "text-warning-dark"}`}>
                      {`PROCEDURE ${index + 1}: `}
                    </h8>
                    <p
                      className="text-start m-0 fontSize12"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      <b>NOTE:</b> {`Procedure ${index + 1} run process time: ${secondMinuteCalculate(item?.runDuration)}.\n`}
                      {item?.exitCode === "result" &&
                        <><b>NOTE:</b> {`This procedure has been successfully executed.\n`}</>
                      }
                      {item?.exitCode === "error" &&
                        <><b>NOTE:</b> {`This procedure did not execute because there was an error in the procedure. Please check it now.\n\n`}</>
                      }
                      <SyntaxHighlighter className="mb-2" language="javascript" style={docco}>
                        {`${item?.code}`}
                      </SyntaxHighlighter>
                      {item?.exitCode === "error" &&
                        item?.syntax?.map((line, key) => (
                          <Form.Label className="m-0" key={key}>
                            <>
                              <b className="text-danger">ERROR:</b>{" "}
                              {`${line}`}
                            </>
                          </Form.Label>
                        ))
                      }
                    </p>
                    {item?.INTABLE?.length > 0 &&
                      <>
                        <Form.Label>
                          <b>NOTE:</b>{" "}
                          {`Read Dataset${item?.INTABLE?.length > 1 ? "s" : ""}\n`}
                        </Form.Label>
                        {item?.INTABLE?.map((tableData, index) => (
                          (tableData?.libraryName && tableData?.observations > 0 && tableData?.variable > 0) &&
                          <Table key={index} className="custom-table border-start border-end">
                            <thead>
                              <tr>
                                <th colSpan={2} className="text-center">{tableData?.libraryName}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Observations</td>
                                <td>{tableData?.observations}</td>
                              </tr>
                              <tr>
                                <td>Variables</td>
                                <td>{tableData?.variable}</td>
                              </tr>
                            </tbody>
                          </Table>
                        ))}
                      </>
                    }
                    {item?.OUTTABLE?.length > 0 &&
                      <>
                        <Form.Label>
                          <b>NOTE:</b>{" "}
                          {`Result Dataset${item?.OUTTABLE?.length > 1 ? "s" : ""}\n`}
                        </Form.Label>
                        {item?.OUTTABLE?.map((tableData, index) => (
                          (tableData?.libraryName && tableData?.observations > 0 && tableData?.variable > 0) &&
                          <Table key={index} className="custom-table border-start border-end">
                            <thead>
                              <tr>
                                <th colSpan={2} className="text-center">{tableData?.libraryName}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Observations</td>
                                <td>{tableData?.observations}</td>
                              </tr>
                              <tr>
                                <td>Variables</td>
                                <td>{tableData?.variable}</td>
                              </tr>
                            </tbody>
                          </Table>
                        ))}
                      </>
                    }
                    {item?.GENREPORT?.length > 0 &&
                      <>
                        <Form.Label>
                          <b>NOTE:</b>{" "}
                          {`Generated Report${item?.GENREPORT?.length > 1 ? "s" : ""}\n`}
                        </Form.Label>
                        {item?.GENREPORT?.map((tableData, index) => (
                          <Table key={index} className="custom-table border-start border-end">
                            <thead>
                              <tr>
                                <th colSpan={2} className="text-center">Generated {(tableData?.type).toUpperCase()} Report Data</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Name</td>
                                <td>{tableData?.name}</td>
                              </tr>
                              <tr>
                                <td>Type</td>
                                <td>.{tableData?.type}</td>
                              </tr>
                              <tr>
                                <td>Size</td>
                                <td>{tableData?.name}</td>
                              </tr>
                              <tr>
                                <td>Location</td>
                                <td>{tableData?.path}</td>
                              </tr>
                            </tbody>
                          </Table>
                        ))}
                      </>
                    }
                  </div>
                ))}
              </div>
            </div>
            :
            <div className="hstack justify-content-center h-100">
              <div className="px-2 fw-bold fontSize12 text-center">
                Log not Found!
              </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default ProjectLogConsole;
